<template>
    <flex-element :element-type="elementType">
        <section class="f-flex">
            <a-text-single-line
                v-for="(element, index) in tabElements"
                :key="element.id"
                v-group:tab-button
                class="f-block f-z-1"
                class-default="f-px-1 f-py-0.5 f-bg-transparent hover:f-bg-gray-200 selected:f-bg-white f-border-1 f-border-solid f-border-transparent selected:f-border-gray-500 selected:f-border-b-0 f-rounded-t-0.25 f-cursor-pointer"
                :class="{ [STATE_CLASSES.SELECTED]: isActiveIndex(index) }"
                :display-name="`${ displayName } - Tab`"
                v-bind="element"
                :index="index"
                :pointer="index + 1"
                is-dynamic-element
                @element-updated="elementUpdated"
                @delete-element="deleteElement"
                @click.native="onTabClick(index)"
            />

            <aside v-if="editingMode" class="controls">
                <button
                    v-if="elementsNew.length > 2"
                    class="controls__button controls__button--remove"
                    type="button"
                    @click.stop="onRemoveTab"
                >
                    <font-awesome-icon :icon="['fas', 'minus-circle']" />
                </button>

                <button
                    class="controls__button controls__button--add"
                    type="button"
                    @click.stop="onAddTab"
                >
                    <font-awesome-icon :icon="['fas', 'plus-circle']" />
                </button>
            </aside>
        </section>

        <o-container
            v-for="(element, index) in paneElements"
            v-show="isActiveIndex(index)"
            :key="element.id"
            v-group:tab-pane
            class-default="f-relative f-p-1 f-w-1/1 f-bg-white f-border-1 f-border-solid f-border-gray-500"
            style="margin-top: -1px"
            :index="index"
            :pointer="index + 1"
            :display-name="`${ displayName } - Pane`"
            v-bind="element"
            @element-updated="elementUpdated"
        >
            <template v-slot="{ elementUpdated, elementsNew }">
                <div class="f-flex f-flex-wrap">
                    <drop-zone-output
                        :elements-new="elementsNew"
                        element-type="drop-zone-output"
                        @element-updated="elementUpdated"
                    />

                    <drop-zone-input :elements-new="elementsNew" />
                </div>
            </template>
        </o-container>
    </flex-element>
</template>

<script>
import { mapMutations, mapState } from 'vuex';
import uuidv4 from 'uuid/v4';
import uniqid from 'uniqid';

import baseOrganism from '@/mixins/base-organism/base-organism-smart';
import { ATextSingleLine, Organism } from '@/data/data-models';
import { CLEAR_SELECTED_ELEMENTS } from '@/store/types/mutation-types';
import flexDeepCopy from '@/utils/flex-deep-copy';
import { STATE_CLASSES } from '@/data/constants/classes';

const publicMode = process.env.VUE_APP_ENV === 'public';

export default {
    name: 'tabbed-nav',
    mixins: [baseOrganism],

    props: {
        displayName: {
            type: String,
            default: 'Tabbed Nav',
        },
    },

    data() {
        return {
            cssDisplay: 'block',
            currentTabIndex: 0,
            itemId: uniqid(),
            STATE_CLASSES,
        };
    },

    computed: {
        ...mapState({
            previewMode: state => state.previewMode,
        }),

        editingMode() {
            return !publicMode && !this.previewMode;
        },

        // even-numbered indices = tab buttons
        tabElements() {
            return this.elementsNew.filter((elem, i) => i % 2 === 0);
        },

        // odd-numbered indices = tab panes
        paneElements() {
            return this.elementsNew.filter((elem, i) => i % 2 !== 0);
        },
    },

    mounted() {
        if (!this.elementsNew.length) this.onAddTab();
    },

    methods: {
        ...mapMutations({
            clearSelectedElements: CLEAR_SELECTED_ELEMENTS,
        }),

        deleteElement(index) {
            this.elementsNew.splice(index, 1);
            this.clearSelectedElements();
            this.setChangedData(true);
        },

        // this adds both a tab and tab pane simultaneously
        onAddTab() {
            const pointer = this.elementsNew.length + 1;
            let tabElement = null;
            let paneElement = null;

            if (this.elementsNew.length) {
                tabElement = { ...this.elementsNew[this.elementsNew.length - 2] };
                tabElement.id = uuidv4();
                tabElement = flexDeepCopy(tabElement);

                paneElement = { ...this.elementsNew[this.elementsNew.length - 1] };
                paneElement.id = uuidv4();
                paneElement = flexDeepCopy(paneElement);
            } else {
                tabElement = new ATextSingleLine({
                    id: uuidv4(),
                    name: `a-text-single-line__${ pointer }`,
                    classes: '',
                    elementType: 'a-text-single-line',
                    pointer,
                    templateId: '00000000-0000-0000-0000-000000000000',
                    workflow: {},
                    singleLineText: this.singleLineTextNew,
                    tag: this.renderTag,
                });

                paneElement = new Organism({
                    id: uuidv4(),
                    name: `o-container_${ pointer }`,
                    elementType: 'o-container',
                    pointer,
                    classes: '',
                    templateType: 'Organism',
                    dataSourceId: '00000000-0000-0000-0000-000000000000',
                });
            }

            this.elementsNew.push(tabElement);
            this.elementsNew.push(paneElement);

            this.setChangedData(true);
        },

        onRemoveTab() {
            this.elementsNew.splice(-2);
        },

        onTabClick(clickedIndex) {
            this.currentTabIndex = clickedIndex;
        },

        isActiveIndex(index) {
            return index === this.currentTabIndex;
        },
    },
};
</script>

<style lang="scss" scoped>
.controls {
    display: flex;
    align-items: center;
    margin-left: auto;

    &__button {
        padding: 0.25rem;

        &:hover {
            cursor: pointer;
        }

        &--add {
            color: var(--form-buttonBackgroundColor);
        }

        &--remove {
            color: var(--form-altButtonBackgroundColor);
            margin-right: 0.5rem;
        }
    }
}
</style>
